import type { IconComponent } from '@alltrails/denali/components/Icon';
import type { Chip } from '@alltrails/denali/components/SelectionChips';
import type { ChipsFilterConfigV2 } from '../../components/FilterControlV2/types';
import type ExploreServiceFilters from '../../types/ExploreServiceFilters';

type Args<T extends string> = {
  collapsedChipCount?: number;
  exploreServiceFilterKey: Extract<keyof ExploreServiceFilters, 'difficulty' | 'feature' | 'routeType' | 'suitability' | 'trailTraffic'>;
  exploreServiceFilters: ExploreServiceFilters;
  getValueIcon?: (value: T) => IconComponent | undefined;
  getValueLabel: (value: T) => string;
  label: string;
  onFiltersChange: (updatedFilters: ExploreServiceFilters) => void;
  values: T[];
};

const useChipsFilterConfig = <T extends string>({
  collapsedChipCount,
  exploreServiceFilterKey,
  exploreServiceFilters,
  getValueIcon,
  getValueLabel,
  label,
  onFiltersChange,
  values
}: Args<T>): ChipsFilterConfigV2 => {
  const selected = (exploreServiceFilters[exploreServiceFilterKey] as T[]) || [];

  const chips: Chip[] = values.map(value => {
    const Component = getValueIcon?.(value);
    return { id: value, label: getValueLabel(value), ...(Component ? { icon: { Component } } : {}) };
  });

  const onChange = (chip: Chip) => {
    const value = chip.id as T;
    const nextSelected = selected.includes(value) ? selected.filter(current => current !== value) : [...selected, value];
    const newFilters = { ...exploreServiceFilters };
    if (nextSelected.length === 0) {
      delete newFilters[exploreServiceFilterKey];
    } else {
      // Cast to Record because the runtime key isn't tied to the generic value type at the type level
      (newFilters as Record<string, unknown>)[exploreServiceFilterKey] = nextSelected;
    }
    onFiltersChange(newFilters);
  };

  return {
    type: 'chips',
    id: exploreServiceFilterKey,
    collapsedChipCount,
    selectionChips: {
      title: label,
      isMultiSelect: true,
      chips,
      selectedChips: chips.filter(chip => selected.includes(chip.id as T)),
      onChange
    }
  };
};

export default useChipsFilterConfig;
